import { Injectable } from '@angular/core';
import { mergeMap, map } from 'rxjs/operators';
import { of, Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ErrorMessageComponent } from 'src/app/shared/presentationals/messages/error-message/error-message.component';
import { NotificationMessageComponent } from 'src/app/shared/presentationals/messages/notification-message/notification-message.component';
import { SuccessMessageComponent } from 'src/app/shared/presentationals/messages/success-message/success-message.component';
import { DirtyComponent } from '../components/dirty.component';
import { ConfirmMessageComponent } from 'src/app/shared/presentationals/messages/confirm-message/confirm-message.component';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef } from '@angular/material/snack-bar';
import { BaseComponent } from '../components/base.component';
import { YesNoCancelMessageComponent } from 'src/app/shared/presentationals/messages/yes-no-cancel-message/yes-no-cancel-message.component';

@Injectable({
  providedIn: 'root'
})
export class MessageService {
  constructor(private readonly snackBar: MatSnackBar,
    private readonly translateService: TranslateService) {
  }

  public openErrorRaw(message: Observable<string>): MatSnackBarRef<ErrorMessageComponent> {
    return this.snackBar.openFromComponent(ErrorMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      data: message,
    });
  }

  public openError(message: string): MatSnackBarRef<ErrorMessageComponent> {
    return this.openErrorRaw(of(message));
  }

  public translateOpenError(key: string, params?: object): MatSnackBarRef<ErrorMessageComponent> {
    return this.openErrorRaw(this.translateService.get(key, params));
  }

  public openNotificationRaw(message: Observable<string>): MatSnackBarRef<NotificationMessageComponent> {
    return this.snackBar.openFromComponent(NotificationMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      data: message,
    });
  }

  public openNotification(message: string): MatSnackBarRef<NotificationMessageComponent> {
    return this.openNotificationRaw(of(message));
  }

  public translateOpenNotification(key: string, params?: object): MatSnackBarRef<NotificationMessageComponent> {
    return this.openNotificationRaw(this.translateService.get(key, params));
  }

  private openSuccessRaw(message: Observable<string>, showDismissIcon: boolean = false, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {

    const config: MatSnackBarConfig = {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      data: { message: message, showDismissIcon: showDismissIcon }
    };

    if (!showDismissIcon) {
      config.duration = duration;
    }

    return this.snackBar.openFromComponent(SuccessMessageComponent, config);
  }

  public openSuccess(message: string, showDismissIcon: boolean = false, duration: number = 3000): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessRaw(of(message), showDismissIcon, duration);
  }

  public translateOpenSuccess(key: string, interpolateParams?: object, showDismissIcon: boolean = false, duration?: number): MatSnackBarRef<SuccessMessageComponent> {
    return this.openSuccessRaw(this.translateService.get(key, interpolateParams), showDismissIcon, duration);
  }

  public openSaveSuccess(showDismissIcon: boolean = false): MatSnackBarRef<SuccessMessageComponent> {
    return this.translateOpenSuccess('Messages.SaveSuccess', {}, showDismissIcon);
  }

  public openDeleteSuccess(showDismissIcon: boolean = false): MatSnackBarRef<SuccessMessageComponent> {
    return this.translateOpenSuccess('Messages.DeleteSuccess', {}, showDismissIcon);
  }

  public openUnexpectedError(): MatSnackBarRef<ErrorMessageComponent> {
    return this.translateOpenError('Messages.UnexpectedError');
  }

  private openConfirmRaw(component: BaseComponent,
    message: Observable<string>,
    primaryButtonLabel: Observable<string>,
    secondaryButtonLabel: Observable<string>,
    next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    const snackBarRef = this.snackBar.openFromComponent(ConfirmMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'show-large-text',
      data: {
        message: message,
        primaryButtonLabel: primaryButtonLabel,
        secondaryButtonLabel: secondaryButtonLabel,
      }
    });

    if (next) {
      snackBarRef.afterDismissed()
        .pipe(map(() => snackBarRef.instance.isConfirmed))
        .unsubscribeOnDestroy(component)
        .subscribe(isConfirmed => {
          next(isConfirmed);
        });
    }

    return snackBarRef;
  }

  public openConfirm(component: BaseComponent,
    message: string,
    primaryButtonLabel: string,
    secondaryButtonLabel: string,
    next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    return this.openConfirmRaw(component, of(message), of(primaryButtonLabel), of(secondaryButtonLabel), next);
  }

  public translateOpenConfirm(component: BaseComponent, messageKey: string, messageInterpolateParams?: object, primaryButtonLabelKey: string = 'Common.Ok', secondaryButtonLabelKey: string = 'Common.Cancel', next?: (isConfirmed: boolean) => void): MatSnackBarRef<ConfirmMessageComponent> {
    return this.openConfirmRaw(component,
      this.translateService.get(messageKey, messageInterpolateParams),
      this.translateService.get(primaryButtonLabelKey),
      this.translateService.get(secondaryButtonLabelKey),
      next);
  }

  public openDeleteConfirm(component: BaseComponent, name: string, onConfirm: () => void) {
    this.translateOpenConfirm(component,
      'Messages.DeleteConfirmation',
      { name: name },
      'Common.Delete',
      'Common.Cancel',
      (isConfirmed: boolean) => {
        if (isConfirmed) {
          onConfirm();
        }
      });
  }

  public openDirtyConfirm(component: DirtyComponent & BaseComponent): Observable<boolean> {
    return component.isDirty.pipe(
      mergeMap(isDirty => {
        if (isDirty) {
          const snackBarRef = this.translateOpenConfirm(component, 'Messages.CancelDirtyConfirmation');

          return snackBarRef
            .afterDismissed()
            .unsubscribeOnDestroy(component)
            .pipe(map(() => {
              return snackBarRef.instance.isConfirmed;
            }));
        }

        return of(true);
      }));
  }

  public dimissOpenedSnackbars(withDurationOnly?: boolean): void {
    if (this.snackBar._openedSnackBarRef) {
      if (withDurationOnly && this.snackBar._openedSnackBarRef?.containerInstance.snackBarConfig.duration === 0) {
        return;
      }

      this.snackBar.dismiss();
    }
  }

  private openYesNoCancelRaw(component: BaseComponent,
    message: Observable<string>,
    yesButtonLabel: Observable<string>,
    noButtonLabel: Observable<string>,
    cancelButtonLabel: Observable<string>,
    next?: (result: 'yes' | 'no' | 'cancel') => void): MatSnackBarRef<YesNoCancelMessageComponent> {

    const snackBarRef = this.snackBar.openFromComponent(YesNoCancelMessageComponent, {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      panelClass: 'show-large-text',
      data: {
        message: message,
        yesLabel: yesButtonLabel,
        noLabel: noButtonLabel,
        cancelLabel: cancelButtonLabel
      }
    });

    if (next) {
      snackBarRef.afterDismissed()
        .pipe(map(() => snackBarRef.instance.result))
        .unsubscribeOnDestroy(component)
        .subscribe((result) => {
          next(result ?? 'cancel');
        });
    }

    return snackBarRef;
  }

  public openYesNoCancel(component: BaseComponent,
    message: string,
    yesButtonLabel: string = 'Common.Yes',
    noButtonLabel: string = 'Common.No',
    cancelButtonLabel: string = 'Common.Cancel',
    next?: (result: 'yes' | 'no' | 'cancel') => void): MatSnackBarRef<YesNoCancelMessageComponent> {
    return this.openYesNoCancelRaw(component, of(message), of(yesButtonLabel), of(noButtonLabel), of(cancelButtonLabel), next);
  }

  public translateOpenYesNoCancel(component: BaseComponent,
    messageKey: string,
    messageInterpolateParams?: object,
    yesButtonLabelKey: string = 'Common.Yes',
    noButtonLabelKey: string = 'Common.No',
    cancelButtonLabelKey: string = 'Common.Cancel',
    next?: (result: 'yes' | 'no' | 'cancel') => void): MatSnackBarRef<YesNoCancelMessageComponent> {
    return this.openYesNoCancelRaw(component,
      this.translateService.get(messageKey, messageInterpolateParams),
      this.translateService.get(yesButtonLabelKey),
      this.translateService.get(noButtonLabelKey),
      this.translateService.get(cancelButtonLabelKey),
      next);
  }
}
